// Outline — the Studio's left column (pen "Studio · Panel · Outline", sized
// by pen Q76): the pages about the material first (Info, Learning
// objectives: a warning dot when one is not covered), a line, then the
// material's tree: the root (named after the project), then its modules and
// files at any depth (studioPages.ts). A container's chevron folds it; a
// page row is its name alone, any other kind (quiz, source, file) its icon
// after the name. A click (or Enter) on a row opens its page; the page shown
// is highlighted. What waits for the author: a dot on the one or two parts
// that wait, or past that one grey count on their container (OutlineMark).
// A sparkle marks a part the assistant planned and the author has not kept
// yet (Suggested). "+ Create" opens the Create menu (StudioCreateMenu.tsx);
// Help and feedback sits at the foot (`help`, the host's). Long names end in
// "…", the full name on hover. Pure: the rows are projected in; an open or a
// fold goes out as an intent.

export { kindIcon, Outline, useAboutLabel };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { formatCount } from "../../app/displayLimits.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import type { DeskAbout, DeskCommand } from "../Studio/studioDesk.js";
import type {
    AboutRow,
    OutlineMark,
    OutlineRow,
    PageNodeKind,
} from "./studioPages.js";

// The icon after a leaf row's name: none for a page (the plain case), the
// kind's own for anything else.
const leafIcon = (kind: PageNodeKind | "root"): IconName | null => {
    switch (kind) {
        case "page":
        case "root":
        case "course":
        case "module":
            return null;
        case "quiz":
        case "source":
        case "file":
            return kindIcon(kind);
        default:
            return exhaustSwitchCase(kind);
    }
};

const kindIcon = (kind: PageNodeKind | "root"): IconName => {
    switch (kind) {
        case "root":
        case "course":
        case "module":
            return "folder";
        case "page":
            return "material";
        case "quiz":
            return "mcq";
        case "source":
            return "source";
        case "file":
            return "file";
        default:
            return exhaustSwitchCase(kind);
    }
};

// A row and the rows inside it (an open folder's parts).
type OutlineNode = {
    readonly row: OutlineRow;
    readonly children: readonly OutlineNode[];
};

// The flat rows (in order, each with its depth) as a tree: a row holds the
// rows after it that lie deeper. O(n²) in the rows at worst (slices); an
// outline holds a few hundred at most.
const nest = (rows: readonly OutlineRow[]): readonly OutlineNode[] => {
    const [first, ...rest] = rows;
    if (first === undefined) return [];
    const end = rest.findIndex((r) => r.depth <= first.depth);
    const inside = end < 0 ? rest : rest.slice(0, end);
    const after = end < 0 ? [] : rest.slice(end);
    return [{ row: first, children: nest(inside) }, ...nest(after)];
};

const Outline = ({
    about,
    rows,
    onOpen,
    onFold,
    foot,
    help,
}: {
    // Above the tree: the pages about the material.
    readonly about: readonly AboutRow[];
    readonly rows: readonly OutlineRow[];
    // Below the rows: "+ Create ▾" and its menu.
    readonly foot: ReactNode;
    // At the outline's foot: Help and feedback (absent: none here).
    readonly help?: ReactNode;
    readonly onOpen: (commands: readonly DeskCommand[]) => void;
    readonly onFold: (key: string, open: boolean) => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const aboutLabel = useAboutLabel();
    // A container's chevron (a button that folds it, when it has parts);
    // a leaf row keeps the chevron's room, so names line up.
    const fold = (row: OutlineRow, open: boolean): ReactNode => {
        const container = kindIcon(row.kind) === "folder";
        if (!container)
            return (
                <span
                    className="bilda-pages__outline-icon"
                    aria-hidden="true"
                />
            );
        const chevron = open ? "chevron-down" : "chevron-right";
        if (row.fold === "leaf")
            return (
                <span className="bilda-pages__outline-icon">
                    <Icon name={chevron} size="xs" />
                </span>
            );
        return (
            <button
                type="button"
                className="bilda-pages__outline-icon bilda-pages__outline-fold"
                aria-expanded={open}
                aria-label={
                    open
                        ? t`Hide the parts of ${row.title}`
                        : t`Show the parts of ${row.title}`
                }
                onClick={() => onFold(row.key, !open)}
            >
                <Icon name={chevron} size="xs" />
            </button>
        );
    };
    const mark = (of: OutlineMark): ReactNode => {
        switch (of.type) {
            case "none":
                return null;
            case "dot":
                return (
                    <span
                        className="bilda-pages__dot"
                        role="img"
                        aria-label={t`Waits for you`}
                    />
                );
            case "count":
                return (
                    <span
                        className="bilda-pages__count"
                        role="img"
                        aria-label={t`${plural(of.count, {
                            one: "# part waits for you",
                            other: "# parts wait for you",
                        })}`}
                    >
                        {formatCount(of.count, i18n.locale)}
                    </span>
                );
            default:
                return exhaustSwitchCase(of);
        }
    };
    const branch = (nodes: readonly OutlineNode[]): ReactNode =>
        nodes.map(({ row, children }) => {
            const after = leafIcon(row.kind);
            return (
                <li key={row.key} data-kind={row.kind}>
                    <div
                        className="bilda-pages__outline-row"
                        aria-current={row.current ? "page" : undefined}
                    >
                        {fold(
                            row,
                            row.kind === "root" ||
                                row.fold === "open" ||
                                children.length > 0,
                        )}
                        <button
                            type="button"
                            className="bilda-pages__outline-open"
                            title={row.title}
                            onClick={() => onOpen(row.open)}
                        >
                            <span className="bilda-pages__outline-title">
                                {row.title}
                            </span>
                            {after === null ? null : (
                                <span className="bilda-pages__outline-kind">
                                    <Icon name={after} size="xs" />
                                </span>
                            )}
                            {row.suggestion ? (
                                <AiMarker
                                    variant="chip"
                                    iconOnly
                                    title={t`Suggested`}
                                />
                            ) : null}
                            {mark(row.mark)}
                        </button>
                    </div>
                    {children.length === 0 ? null : (
                        <ul className="bilda-pages__outline-parts">
                            {branch(children)}
                        </ul>
                    )}
                </li>
            );
        });
    return (
        <nav className="bilda-pages__outline" aria-label={t`Outline`}>
            <ul className="bilda-pages__outline-about">
                {about.map((row) => {
                    const [name, label] = aboutLabel(row.id);
                    return (
                        <li key={row.id}>
                            <button
                                type="button"
                                className="bilda-pages__outline-row bilda-pages__outline-open"
                                aria-current={row.current ? "page" : undefined}
                                onClick={() => onOpen(row.open)}
                            >
                                <Icon name={name} size="xs" />
                                <span className="bilda-pages__outline-title">
                                    {label}
                                </span>
                                {row.warning ? (
                                    <span
                                        className="bilda-pages__dot"
                                        data-tone="warning"
                                        role="img"
                                        aria-label={t`An objective is not covered`}
                                    />
                                ) : null}
                            </button>
                        </li>
                    );
                })}
            </ul>
            <ul className="bilda-pages__outline-rows">{branch(nest(rows))}</ul>
            <div className="bilda-pages__outline-foot">{foot}</div>
            {help === undefined ? null : (
                <div className="bilda-pages__outline-help">{help}</div>
            )}
        </nav>
    );
};

// A page about the material: its icon and its word (the outline's rows, the
// top bar's breadcrumb).
const useAboutLabel = (): ((id: DeskAbout) => readonly [IconName, string]) => {
    const { t } = useLingui();
    return (id) => {
        switch (id) {
            case "info":
                return ["info", t`Info`];
            case "objectives":
                return ["target", t`Learning objectives`];
            case "sources":
                return ["document", t`Sources`];
            default:
                return exhaustSwitchCase(id);
        }
    };
};
